import { gql, useQuery } from "@apollo/client";
import { useCallback, useMemo } from "react";
import type { ProjectSpaceData } from "./useProjectSpace";
import { useProjectTaskTemplateSetId } from "@/components/projects/hooks/useProjectTaskTemplateSetId";
import { useProjectInitData } from "@/components/projects/hooks/useProjectInitData";
import type {
  PlotState,
  ProcurementPackageState,
  WorkSectionState,
  SpaceState,
  SpaceResponsibleState,
  SchedulingSpace,
  TemplateTaskAvailabilityMaps,
} from "@/components/projects/hooks/useProjectInitData";

const GET_DIALOG_PPS = gql`
  query GetDialogProcurementPackages($limit: Int) {
    procurementPackages(limit: $limit) {
      items {
        id
        code
        name
        plotId
        plannedStart
        plannedEnd
        responsibleUserId
        templateProcurementPackageId
        version
      }
    }
  }
`;

const GET_DIALOG_SECTIONS = gql`
  query GetDialogSections($limit: Int) {
    sections(limit: $limit) {
      items {
        id
        version
        code
        name
        procurementPackageId
        templateWorkSectionId
        plannedStart
        plannedEnd
        responsibleUserId
      }
    }
  }
`;

const GET_DIALOG_SECTION_LOCATIONS = gql`
  query GetDialogSectionLocations($limit: Int) {
    sectionLocations(limit: $limit) {
      items {
        id
        version
        sectionId
        locationId
        plannedStart
        plannedEnd
        responsibleUserId
      }
    }
  }
`;

const GET_DIALOG_PP_TASKS = gql`
  query GetDialogPpTasks($projectId: String!) {
    tasks(filter: { projectId: $projectId, parentIdIsNull: true }, limit: 3000) {
      items {
        id
        title
        procurementPackageId
        sectionId
        locationId
      }
    }
  }
`;

interface DialogPP {
  id: string;
  code: string;
  name: string | null;
  plotId: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  responsibleUserId: string | null;
  templateProcurementPackageId: string;
  version: number;
}

interface DialogSection {
  id: string;
  version: number;
  code: string | null;
  name: string;
  procurementPackageId: string | null;
  templateWorkSectionId: string | null;
  plannedStart: string | null;
  plannedEnd: string | null;
  responsibleUserId: string | null;
}

interface DialogSectionLocation {
  id: string;
  version: number;
  sectionId: string;
  locationId: string;
  plannedStart: string | null;
  plannedEnd: string | null;
  responsibleUserId: string | null;
}

export interface PpMeta {
  id: string;
  version: number;
  originalResponsibleUserId: string | null;
}

export interface WsMeta {
  id: string;
  version: number;
  originalInspectorUserId: string | null;
}

export interface ProjectDialogData {
  plots: PlotState[];
  /** ppMetaMatrix[plotIdx][ppIdx] — parallel to plots, used for save comparisons */
  ppMetaMatrix: PpMeta[][];
  /** wsMetaMatrix[plotIdx][ppIdx][wsIdx] — parallel to plots, used for save comparisons */
  wsMetaMatrix: WsMeta[][][];
  spaceIdToLocationId: Map<string, string>;
  /** Existing main-task id, keyed by `${ppId}:${title}` — used to resolve which
   *  PP-level task to delete when its checkbox is unchecked in edit mode. */
  taskIdByPpTitle: Map<string, string>;
  /** Existing main-task id, keyed by `${sectionId}:${locationId}:${title}` —
   *  used to resolve which WS/space-level task to delete on uncheck. */
  taskIdBySecLocTitle: Map<string, string>;
  allSchedulingSpaces: SchedulingSpace[];
  templateTaskAvailability: TemplateTaskAvailabilityMaps;
  /** Fast: internal DB queries (PP list, sections, SL, tasks, project space). */
  loadingBasic: boolean;
  /** True only once the project space AND all four internal DB queries have
   *  actually returned data. Unlike `!loadingBasic`, this is *not* satisfied
   *  while the queries are skipped (a skipped Apollo query reports
   *  loading=false with no data), so it is the correct signal for "the seeded
   *  `plots` contain real procurement packages, not just projectSpace shells". */
  basicLoaded: boolean;
  /** Slow: external Sitedrive + template waterfall (needed for column 4). */
  loadingDetail: boolean;
  /** Refetch the internal DB queries (PPs, sections, section-locations) so the
   *  seeded `plots` reflect freshly persisted responsibles. A created
   *  section-location can't be auto-inserted into a cached list, so callers
   *  must refetch after the upsert mutation rather than rely on normalisation. */
  refetch: () => Promise<void>;
}

export function useProjectDialogData(params: {
  projectId: string | null;
  projectSpace: ProjectSpaceData | null;
  spaceLoading: boolean;
}): ProjectDialogData {
  const { projectId, projectSpace, spaceLoading } = params;

  const { data: ppData, loading: ppLoading, refetch: refetchPps } = useQuery<{
    procurementPackages: { items: DialogPP[] };
  }>(GET_DIALOG_PPS, {
    variables: { limit: 500 },
    fetchPolicy: "cache-and-network",
    skip: !projectId,
  });

  const { data: sectionData, loading: sectionLoading, refetch: refetchSections } = useQuery<{
    sections: { items: DialogSection[] };
  }>(GET_DIALOG_SECTIONS, {
    variables: { limit: 2000 },
    fetchPolicy: "cache-and-network",
    skip: !projectId,
  });

  const { data: slData, loading: slLoading, refetch: refetchSectionLocations } = useQuery<{
    sectionLocations: { items: DialogSectionLocation[] };
  }>(GET_DIALOG_SECTION_LOCATIONS, {
    variables: { limit: 10000 },
    fetchPolicy: "cache-and-network",
    skip: !projectId,
  });

  const { data: ppTaskData, loading: ppTaskLoading, refetch: refetchPpTasks } = useQuery<{
    tasks: { items: Array<{ id: string; title: string; procurementPackageId: string | null; sectionId: string | null; locationId: string | null }> };
  }>(GET_DIALOG_PP_TASKS, {
    variables: { projectId },
    fetchPolicy: "cache-and-network",
    skip: !projectId,
  });

  const { taskTemplateSetId, loading: ttsLoading } = useProjectTaskTemplateSetId(projectId);

  const {
    templateTaskAvailability,
    loading: initLoading,
  } = useProjectInitData(null, taskTemplateSetId);

  const allSchedulingSpaces = useMemo<SchedulingSpace[]>(() => {
    if (!projectSpace) return [];
    const allLocations = [
      ...projectSpace.plots.flatMap((p) => p.locations),
      ...projectSpace.locations,
    ];
    return allLocations
      .filter((loc) => loc.schedulingPlatformSpaceId)
      .map((loc) => ({
        id: loc.schedulingPlatformSpaceId!,
        name: loc.name,
        parentId: loc.schedulingPlatformSpaceParentId ?? null,
        orderNumber: null,
        type: loc.locationType ?? null,
      }));
  }, [projectSpace]);

  const allPPs = ppData?.procurementPackages.items ?? [];
  const allSections = sectionData?.sections.items ?? [];
  const allSectionLocations = slData?.sectionLocations.items ?? [];
  const allPpTasks = ppTaskData?.tasks.items ?? [];

  const { plots, ppMetaMatrix, wsMetaMatrix, spaceIdToLocationId } = useMemo(() => {
    if (!projectSpace) return { plots: [] as PlotState[], ppMetaMatrix: [] as PpMeta[][], wsMetaMatrix: [] as WsMeta[][][], spaceIdToLocationId: new Map<string, string>() };

    const ppTaskTitlesByPpId = new Map<string, Set<string>>();
    const wsTasksBySecLoc = new Map<string, Set<string>>();
    for (const task of allPpTasks) {
      if (task.procurementPackageId) {
        const s = ppTaskTitlesByPpId.get(task.procurementPackageId) ?? new Set<string>();
        s.add(task.title);
        ppTaskTitlesByPpId.set(task.procurementPackageId, s);
      }
      if (task.sectionId && task.locationId) {
        const key = `${task.sectionId}:${task.locationId}`;
        const s = wsTasksBySecLoc.get(key) ?? new Set<string>();
        s.add(task.title);
        wsTasksBySecLoc.set(key, s);
      }
    }

    const locationSpaceMap = new Map<string, string>();
    for (const plot of projectSpace.plots) {
      for (const loc of plot.locations) {
        if (loc.schedulingPlatformSpaceId) {
          locationSpaceMap.set(loc.id, loc.schedulingPlatformSpaceId);
        }
      }
    }
    for (const loc of projectSpace.locations) {
      if (loc.schedulingPlatformSpaceId) {
        locationSpaceMap.set(loc.id, loc.schedulingPlatformSpaceId);
      }
    }

    const spaceIdToLocationId = new Map<string, string>();
    for (const [locationId, spaceId] of locationSpaceMap.entries()) {
      spaceIdToLocationId.set(spaceId, locationId);
    }

    const slBySectionId = new Map<string, DialogSectionLocation[]>();
    for (const sl of allSectionLocations) {
      const arr = slBySectionId.get(sl.sectionId) ?? [];
      arr.push(sl);
      slBySectionId.set(sl.sectionId, arr);
    }

    const sectionsByPpId = new Map<string, DialogSection[]>();
    for (const section of allSections) {
      if (!section.procurementPackageId) continue;
      const arr = sectionsByPpId.get(section.procurementPackageId) ?? [];
      arr.push(section);
      sectionsByPpId.set(section.procurementPackageId, arr);
    }

    const resultPlots: PlotState[] = [];
    const resultMatrix: PpMeta[][] = [];
    const resultWsMatrix: WsMeta[][][] = [];

    for (const plot of projectSpace.plots) {
      const plotPPs = allPPs.filter((pp) => pp.plotId === plot.id);
      const plotPpMeta: PpMeta[] = [];
      const plotWsMeta: WsMeta[][] = [];
      const procurementPackages: ProcurementPackageState[] = [];

      for (const pp of plotPPs) {
        plotPpMeta.push({
          id: pp.id,
          version: pp.version,
          originalResponsibleUserId: pp.responsibleUserId,
        });

        const sections = sectionsByPpId.get(pp.id) ?? [];
        plotWsMeta.push(sections.map((section) => ({
          id: section.id,
          version: section.version,
          originalInspectorUserId: section.responsibleUserId,
        })));
        const workSections: WorkSectionState[] = sections.map((section) => {
          const sls = slBySectionId.get(section.id) ?? [];
          const spaceResponsibles: Record<string, SpaceResponsibleState> = {};
          for (const sl of sls) {
            const schedulingSpaceId = locationSpaceMap.get(sl.locationId);
            if (!schedulingSpaceId) continue;
            spaceResponsibles[schedulingSpaceId] = {
              locationId: sl.locationId,
              responsibleUserId: sl.responsibleUserId,
              sectionLocationId: sl.id,
              sectionLocationVersion: sl.version,
              originalResponsibleUserId: sl.responsibleUserId,
            };
          }
          const spaces: SpaceState[] = sls
            .map((sl) => {
              const schedulingSpaceId = locationSpaceMap.get(sl.locationId);
              if (!schedulingSpaceId) return null;
              const wsTasks =
                wsTasksBySecLoc.get(`${section.id}:${sl.locationId}`) ??
                new Set<string>();
              return {
                schedulingSpaceId,
                schedulingTaskIds: [],
                plannedStart: sl.plannedStart ?? "",
                plannedEnd: sl.plannedEnd ?? "",
                includeOsakohteenTarkastus: wsTasks.has("Osakohteen tarkastus"),
                includeMalliasennustarkastus: wsTasks.has("Malliasennustarkastus"),
                includeMestanVastaanotto: wsTasks.has("Mestan vastaanotto"),
                includeTyovaiheenvastaanotto: wsTasks.has("Työvaiheenvastaanotto"),
                includeLiitedokumentit: wsTasks.has("Liitedokumentit"),
              } as SpaceState;
            })
            .filter((s): s is SpaceState => s !== null);

          return {
            sectionCode: section.code ?? section.id,
            sectionName: section.name,
            templateWorkSectionId: section.templateWorkSectionId ?? undefined,
            spaces,
            spaceResponsibles,
            plannedStart: section.plannedStart,
            plannedEnd: section.plannedEnd,
            inspectorUserId: section.responsibleUserId,
          } as WorkSectionState;
        });

        const ppTitles = ppTaskTitlesByPpId.get(pp.id) ?? new Set<string>();
        procurementPackages.push({
          templateProcurementPackageId: pp.templateProcurementPackageId,
          code: pp.code,
          name: pp.name,
          responsibleUserId: pp.responsibleUserId,
          includeSuunnitelmiin: ppTitles.has("Suunnitelmiin perehtyminen"),
          includeAliurakka: ppTitles.has("Aliurakkasopimukseen perehtyminen"),
          includeAloitusedellytykset: ppTitles.has("Aloitusedellytykset"),
          includeTaloudellinenLoppuselvitys: ppTitles.has("Taloudellinen loppuselvitys"),
          workSections,
          plannedStart: pp.plannedStart,
          plannedEnd: pp.plannedEnd,
        } as ProcurementPackageState);
      }

      resultPlots.push({
        name: plot.name,
        locationSchedulingSpaceId: null,
        procurementPackages,
      } as PlotState);
      resultMatrix.push(plotPpMeta);
      resultWsMatrix.push(plotWsMeta);
    }

    return { plots: resultPlots, ppMetaMatrix: resultMatrix, wsMetaMatrix: resultWsMatrix, spaceIdToLocationId };
  }, [projectSpace, allPPs, allSections, allSectionLocations, allPpTasks]);

  // Id lookups for deletion: resolve an unchecked task's DB id from its
  // (PP, title) or (section, location, title) coordinates.
  const { taskIdByPpTitle, taskIdBySecLocTitle } = useMemo(() => {
    const byPp = new Map<string, string>();
    const bySecLoc = new Map<string, string>();
    for (const task of allPpTasks) {
      if (task.procurementPackageId) {
        byPp.set(`${task.procurementPackageId}:${task.title}`, task.id);
      }
      if (task.sectionId && task.locationId) {
        bySecLoc.set(`${task.sectionId}:${task.locationId}:${task.title}`, task.id);
      }
    }
    return { taskIdByPpTitle: byPp, taskIdBySecLocTitle: bySecLoc };
  }, [allPpTasks]);

  const refetch = useCallback(async () => {
    // Section-locations carry the responsibles; sections and PPs are refetched
    // too so the seeded `plots` stay internally consistent after a save. The
    // PP-task query is refetched as well so task deletions are reflected in the
    // include* checkbox flags (which derive from it).
    await Promise.all([
      refetchSectionLocations(),
      refetchSections(),
      refetchPps(),
      refetchPpTasks(),
    ]);
  }, [refetchSectionLocations, refetchSections, refetchPps, refetchPpTasks]);

  return {
    plots,
    ppMetaMatrix,
    wsMetaMatrix,
    spaceIdToLocationId,
    taskIdByPpTitle,
    taskIdBySecLocTitle,
    allSchedulingSpaces,
    templateTaskAvailability,
    loadingBasic:
      spaceLoading || ppLoading || sectionLoading || slLoading || ppTaskLoading,
    basicLoaded:
      !!projectSpace && !!ppData && !!sectionData && !!slData && !!ppTaskData,
    loadingDetail: ttsLoading || initLoading,
    refetch,
  };
}
